import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import useSettingsStore from './store/useSettingsStore';
import Header from './components/Header';
import Footer from './components/Footer';
import FeedbackButton from './components/FeedbackButton';
import { PageTransition } from './components/PageTransition';
import { Toaster } from 'sonner';
import './App.css';

// Lazy load pages
const Home = lazy(() => import('./pages/Home'));
const Catalog = lazy(() => import('./pages/Catalog'));
const CarDetail = lazy(() => import('./pages/CarDetail'));
const Configurator = lazy(() => import('./pages/Configurator'));
const Services = lazy(() => import('./pages/Services'));
const Reviews = lazy(() => import('./pages/Reviews'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const About = lazy(() => import('./pages/About'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Login = lazy(() => import('./pages/Login'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Layout({ children }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/administrator-panel');

  if (isAdmin) {
    return <>{children}</>;
  }

  return (
    <>
      <Header />
      <main className="min-h-screen pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
      <FeedbackButton />
    </>
  );
}

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-dark)' }}>
    <div className="text-center">
      <div className="w-12 h-12 border-2 rounded-full animate-spin mx-auto mb-4"
           style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-accent-gold)' }} />
      <p style={{ color: 'var(--color-text-muted)' }}>Загрузка...</p>
    </div>
  </div>
);

function AnimatedRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/administrator-panel');

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Home /></PageTransition>} />
        <Route path="/catalog" element={<PageTransition><Catalog /></PageTransition>} />
        <Route path="/cars/:slug" element={<PageTransition><CarDetail /></PageTransition>} />
        <Route path="/configurator" element={<PageTransition><Configurator /></PageTransition>} />
        <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
        <Route path="/reviews" element={<PageTransition><Reviews /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><Blog /></PageTransition>} />
        <Route path="/blog/:slug" element={<PageTransition><BlogArticle /></PageTransition>} />
        <Route path="/about" element={<PageTransition><About /></PageTransition>} />
        <Route path="/contacts" element={<PageTransition><Contacts /></PageTransition>} />
        <Route path="/administrator-panel" element={isAdmin ? <Login /> : <PageTransition><Login /></PageTransition>} />
        <Route path="/administrator-panel/dashboard" element={<AdminPanel />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const { fetchSettings, loading } = useSettingsStore();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (loading) return <Loading />;

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--color-primary-light)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          },
        }}
      />
      <Layout>
        <Suspense fallback={<Loading />}>
          <AnimatedRoutes />
        </Suspense>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
